import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  X, Plus, Users, Heart, HandHeart, BookOpen, Trophy, 
  Star, Briefcase, Loader2, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Predefined section types
const SECTION_TYPES = {
  references: {
    icon: Users,
    color: 'bg-blue-500',
    labelFr: 'Références',
    labelEn: 'References',
    descFr: 'Contacts professionnels qui peuvent vous recommander',
    descEn: 'Professional contacts who can recommend you'
  },
  interests: {
    icon: Heart,
    color: 'bg-pink-500',
    labelFr: 'Centres d\'intérêt',
    labelEn: 'Interests',
    descFr: 'Hobbies et passions',
    descEn: 'Hobbies and passions'
  },
  volunteering: {
    icon: HandHeart,
    color: 'bg-green-500',
    labelFr: 'Bénévolat',
    labelEn: 'Volunteering',
    descFr: 'Engagements associatifs et humanitaires',
    descEn: 'Charitable and community work'
  },
  publications: {
    icon: BookOpen,
    color: 'bg-purple-500',
    labelFr: 'Publications',
    labelEn: 'Publications',
    descFr: 'Articles, livres, recherches publiées',
    descEn: 'Articles, books, published research'
  },
  custom: {
    icon: Star,
    color: 'bg-orange-500',
    labelFr: 'Section personnalisée',
    labelEn: 'Custom Section',
    descFr: 'Créer une section libre',
    descEn: 'Create a free-form section'
  }
};

/**
 * AddSectionModal - Modal for adding new CV sections
 */
export default function AddSectionModal({ isOpen, onClose, profileId, onSectionAdded, initialType = null }) {
  const { language } = useLanguage();
  const [selectedType, setSelectedType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({});
  
  // Reset state when modal opens/closes or initialType changes
  React.useEffect(() => {
    if (isOpen) {
      // If initialType is provided, select that type
      // Otherwise, reset to type selector view
      if (initialType && SECTION_TYPES[initialType]) {
        setSelectedType(initialType);
      } else {
        setSelectedType(null);
      }
      setFormData({});
    }
  }, [isOpen, initialType]);
  
  const isEn = language === 'en';

  const translations = {
    fr: {
      title: 'Ajouter une section',
      subtitle: 'Enrichissez votre profil avec des informations supplémentaires',
      selectType: 'Choisissez un type de section',
      back: 'Retour',
      add: 'Ajouter',
      cancel: 'Annuler',
      // Reference form
      refName: 'Nom complet',
      refPosition: 'Poste',
      refCompany: 'Entreprise',
      refEmail: 'Email',
      refPhone: 'Téléphone',
      refRelationship: 'Relation (ex: Ancien manager)',
      // Interest form
      interestPlaceholder: 'Séparez vos centres d\'intérêt par des virgules (ex: Photographie, Lecture, Cuisine)',
      // Volunteering form
      volOrg: 'Organisation',
      volRole: 'Rôle',
      volDates: 'Période (ex: 2020 - 2023)',
      volDesc: 'Description',
      // Publication form
      pubTitle: 'Titre',
      pubPublisher: 'Éditeur / Journal',
      pubDate: 'Date',
      pubUrl: 'URL (optionnel)',
      pubDesc: 'Description',
      // Custom section form
      customTitle: 'Titre de la section',
      customIcon: 'Emoji/Icône',
      customItemTitle: 'Titre de l\'élément',
      customItemSubtitle: 'Sous-titre (optionnel)',
      customItemDesc: 'Description (optionnel)',
      addItem: 'Ajouter un élément',
      // Success messages
      successRef: 'Référence ajoutée !',
      successInterest: 'Centres d\'intérêt mis à jour !',
      successVol: 'Bénévolat ajouté !',
      successPub: 'Publication ajoutée !',
      successCustom: 'Section ajoutée !',
      error: 'Erreur lors de l\'ajout'
    },
    en: {
      title: 'Add a Section',
      subtitle: 'Enhance your profile with additional information',
      selectType: 'Choose a section type',
      back: 'Back',
      add: 'Add',
      cancel: 'Cancel',
      // Reference form
      refName: 'Full name',
      refPosition: 'Position',
      refCompany: 'Company',
      refEmail: 'Email',
      refPhone: 'Phone',
      refRelationship: 'Relationship (e.g., Former manager)',
      // Interest form
      interestPlaceholder: 'Separate your interests with commas (e.g., Photography, Reading, Cooking)',
      // Volunteering form
      volOrg: 'Organization',
      volRole: 'Role',
      volDates: 'Period (e.g., 2020 - 2023)',
      volDesc: 'Description',
      // Publication form
      pubTitle: 'Title',
      pubPublisher: 'Publisher / Journal',
      pubDate: 'Date',
      pubUrl: 'URL (optional)',
      pubDesc: 'Description',
      // Custom section form
      customTitle: 'Section title',
      customIcon: 'Emoji/Icon',
      customItemTitle: 'Item title',
      customItemSubtitle: 'Subtitle (optional)',
      customItemDesc: 'Description (optional)',
      addItem: 'Add an item',
      // Success messages
      successRef: 'Reference added!',
      successInterest: 'Interests updated!',
      successVol: 'Volunteering added!',
      successPub: 'Publication added!',
      successCustom: 'Section added!',
      error: 'Error adding section'
    }
  };

  const t = translations[language] || translations.en;

  const resetForm = () => {
    setSelectedType(null);
    setFormData({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!profileId || !selectedType) return;
    
    setIsLoading(true);
    try {
      let endpoint = '';
      let body = {};
      let successMsg = '';

      switch (selectedType) {
        case 'references':
          endpoint = `/api/profiles/${profileId}/references`;
          body = {
            name: formData.name || '',
            position: formData.position || null,
            company: formData.company || null,
            email: formData.email || null,
            phone: formData.phone || null,
            relationship: formData.relationship || null
          };
          successMsg = t.successRef;
          break;

        case 'interests':
          endpoint = `/api/profiles/${profileId}/interests`;
          const interests = (formData.interests || '')
            .split(',')
            .map(i => i.trim())
            .filter(i => i.length > 0);
          body = interests;
          successMsg = t.successInterest;
          break;

        case 'volunteering':
          endpoint = `/api/profiles/${profileId}/volunteering`;
          body = {
            organization: formData.organization || '',
            role: formData.role || null,
            start_date: formData.startDate || null,
            end_date: formData.endDate || null,
            description: formData.description || null
          };
          successMsg = t.successVol;
          break;

        case 'publications':
          endpoint = `/api/profiles/${profileId}/publications`;
          body = {
            title: formData.title || '',
            publisher: formData.publisher || null,
            date: formData.date || null,
            url: formData.url || null,
            description: formData.description || null
          };
          successMsg = t.successPub;
          break;

        case 'custom':
          endpoint = `/api/profiles/${profileId}/custom-sections`;
          body = {
            title: formData.sectionTitle || '',
            icon: formData.icon || '📋',
            items: formData.items || [],
            display_order: 100
          };
          successMsg = t.successCustom;
          break;

        default:
          return;
      }

      const method = selectedType === 'interests' ? 'PUT' : 'POST';
      const response = await fetch(`${API_URL}${endpoint}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error('Failed to add section');
      }

      toast.success(successMsg);
      onSectionAdded?.();
      handleClose();

    } catch (error) {
      console.error('Error adding section:', error);
      toast.error(t.error);
    } finally {
      setIsLoading(false);
    }
  };

  // Render section type selector
  const renderTypeSelector = () => (
    <div className="p-3 sm:p-5">
      <p className="text-sm text-gray-500 mb-3 sm:mb-4">{t.selectType}</p>
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        {Object.entries(SECTION_TYPES).map(([key, config]) => {
          const Icon = config.icon;
          return (
            <button
              key={key}
              onClick={() => setSelectedType(key)}
              className="flex flex-col items-center p-3 sm:p-4 rounded-xl border-2 border-gray-200 hover:border-[#5BA946] hover:bg-green-50 active:bg-green-100 transition-all group touch-manipulation"
              data-testid={`section-type-${key}`}
            >
              <div className={`w-10 h-10 sm:w-12 sm:h-12 ${config.color} rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform`}>
                <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="font-medium text-xs sm:text-sm text-gray-800 text-center">
                {isEn ? config.labelEn : config.labelFr}
              </span>
              <span className="text-[10px] sm:text-xs text-gray-500 text-center mt-1 line-clamp-2">
                {isEn ? config.descEn : config.descFr}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  // Render reference form
  const renderReferenceForm = () => (
    <div className="p-3 sm:p-5 space-y-3 sm:space-y-4">
      <Input
        placeholder={t.refName}
        value={formData.name || ''}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        className="text-base h-11"
        style={{ fontSize: '16px' }}
        data-testid="ref-name-input"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
        <Input
          placeholder={t.refPosition}
          value={formData.position || ''}
          onChange={(e) => setFormData({ ...formData, position: e.target.value })}
          className="text-base h-11"
          style={{ fontSize: '16px' }}
        />
        <Input
          placeholder={t.refCompany}
          value={formData.company || ''}
          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
          className="text-base h-11"
          style={{ fontSize: '16px' }}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
        <Input
          placeholder={t.refEmail}
          type="email"
          value={formData.email || ''}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="text-base h-11"
          style={{ fontSize: '16px' }}
        />
        <Input
          placeholder={t.refPhone}
          value={formData.phone || ''}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          className="text-base h-11"
          style={{ fontSize: '16px' }}
        />
      </div>
      <Input
        placeholder={t.refRelationship}
        value={formData.relationship || ''}
        onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
        className="text-base h-11"
        style={{ fontSize: '16px' }}
      />
    </div>
  );

  // Render interests form
  const renderInterestsForm = () => (
    <div className="p-3 sm:p-5">
      <Textarea
        placeholder={t.interestPlaceholder}
        value={formData.interests || ''}
        onChange={(e) => setFormData({ ...formData, interests: e.target.value })}
        rows={4}
        className="text-base"
        style={{ fontSize: '16px' }}
        data-testid="interests-input"
      />
      {formData.interests && (
        <div className="flex flex-wrap gap-2 mt-3">
          {formData.interests.split(',').map((interest, idx) => {
            const trimmed = interest.trim();
            if (!trimmed) return null;
            return (
              <span key={idx} className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm">
                {trimmed}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );

  // Render volunteering form
  const renderVolunteeringForm = () => (
    <div className="p-3 sm:p-5 space-y-3 sm:space-y-4">
      <Input
        placeholder={t.volOrg}
        value={formData.organization || ''}
        onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
        className="text-base h-11"
        style={{ fontSize: '16px' }}
        data-testid="vol-org-input"
      />
      <Input
        placeholder={t.volRole}
        value={formData.role || ''}
        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
        className="text-base h-11"
        style={{ fontSize: '16px' }}
      />
      <Input
        placeholder={t.volDates}
        value={formData.dates || ''}
        onChange={(e) => setFormData({ ...formData, dates: e.target.value })}
        className="text-base h-11"
        style={{ fontSize: '16px' }}
      />
      <Textarea
        placeholder={t.volDesc}
        value={formData.description || ''}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        rows={3}
        className="text-base"
        style={{ fontSize: '16px' }}
      />
    </div>
  );

  // Render publications form
  const renderPublicationsForm = () => (
    <div className="p-3 sm:p-5 space-y-3 sm:space-y-4">
      <Input
        placeholder={t.pubTitle}
        value={formData.title || ''}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        className="text-base h-11"
        style={{ fontSize: '16px' }}
        data-testid="pub-title-input"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
        <Input
          placeholder={t.pubPublisher}
          value={formData.publisher || ''}
          onChange={(e) => setFormData({ ...formData, publisher: e.target.value })}
          className="text-base h-11"
          style={{ fontSize: '16px' }}
        />
        <Input
          placeholder={t.pubDate}
          value={formData.date || ''}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
          className="text-base h-11"
          style={{ fontSize: '16px' }}
        />
      </div>
      <Input
        placeholder={t.pubUrl}
        value={formData.url || ''}
        onChange={(e) => setFormData({ ...formData, url: e.target.value })}
        className="text-base h-11"
        style={{ fontSize: '16px' }}
      />
      <Textarea
        placeholder={t.pubDesc}
        value={formData.description || ''}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        rows={2}
        className="text-base"
        style={{ fontSize: '16px' }}
      />
    </div>
  );

  // Render custom section form
  const renderCustomForm = () => {
    const items = formData.items || [];
    
    const addItem = () => {
      setFormData({
        ...formData,
        items: [...items, { title: '', subtitle: '', description: '', date: '' }]
      });
    };

    const updateItem = (index, field, value) => {
      const newItems = [...items];
      newItems[index] = { ...newItems[index], [field]: value };
      setFormData({ ...formData, items: newItems });
    };

    const removeItem = (index) => {
      const newItems = items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    };

    return (
      <div className="p-3 sm:p-5 space-y-3 sm:space-y-4">
        <div className="flex gap-2 sm:gap-3">
          <div className="w-16 sm:w-20">
            <Input
              placeholder="Icon"
              value={formData.icon || ''}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              className="text-center text-xl h-11"
              style={{ fontSize: '16px' }}
              maxLength={2}
            />
          </div>
          <div className="flex-1">
            <Input
              placeholder={t.customTitle}
              value={formData.sectionTitle || ''}
              onChange={(e) => setFormData({ ...formData, sectionTitle: e.target.value })}
              className="text-base h-11"
              style={{ fontSize: '16px' }}
              data-testid="custom-title-input"
            />
          </div>
        </div>

        <div className="space-y-3 max-h-[250px] sm:max-h-[300px] overflow-y-auto overscroll-contain">
          {items.map((item, index) => (
            <div key={index} className="p-2 sm:p-3 bg-gray-50 rounded-lg space-y-2 relative">
              <button
                onClick={() => removeItem(index)}
                className="absolute top-2 right-2 text-gray-400 hover:text-red-500 p-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <Input
                placeholder={t.customItemTitle}
                value={item.title || ''}
                onChange={(e) => updateItem(index, 'title', e.target.value)}
                className="text-base h-10"
                style={{ fontSize: '16px' }}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input
                  placeholder={t.customItemSubtitle}
                  value={item.subtitle || ''}
                  onChange={(e) => updateItem(index, 'subtitle', e.target.value)}
                  className="text-base h-10"
                  style={{ fontSize: '16px' }}
                />
                <Input
                  placeholder="Date"
                  value={item.date || ''}
                  onChange={(e) => updateItem(index, 'date', e.target.value)}
                  className="text-base h-10"
                  style={{ fontSize: '16px' }}
                />
              </div>
              <Textarea
                placeholder={t.customItemDesc}
                value={item.description || ''}
                onChange={(e) => updateItem(index, 'description', e.target.value)}
                rows={2}
                className="text-base"
                style={{ fontSize: '16px' }}
              />
            </div>
          ))}
        </div>

        <Button
          variant="outline"
          onClick={addItem}
          className="w-full gap-2 h-10"
          data-testid="add-custom-item-btn"
        >
          <Plus className="w-4 h-4" />
          {t.addItem}
        </Button>
      </div>
    );
  };

  // Render form based on selected type
  const renderForm = () => {
    switch (selectedType) {
      case 'references':
        return renderReferenceForm();
      case 'interests':
        return renderInterestsForm();
      case 'volunteering':
        return renderVolunteeringForm();
      case 'publications':
        return renderPublicationsForm();
      case 'custom':
        return renderCustomForm();
      default:
        return renderTypeSelector();
    }
  };

  // Check if form is valid
  const isFormValid = () => {
    switch (selectedType) {
      case 'references':
        return formData.name?.trim();
      case 'interests':
        return formData.interests?.trim();
      case 'volunteering':
        return formData.organization?.trim();
      case 'publications':
        return formData.title?.trim();
      case 'custom':
        return formData.sectionTitle?.trim() && (formData.items?.length > 0);
      default:
        return false;
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal - Mobile optimized */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scaleIn max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        {/* Header - Compact on mobile */}
        <div className="bg-gradient-to-r from-[#5BA946] to-emerald-600 p-4 sm:p-5 text-center relative flex-shrink-0">
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 sm:top-3 sm:right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white"
            data-testid="close-add-section-modal"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-2 sm:mb-3 rounded-2xl bg-white/20 flex items-center justify-center">
            <Plus className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
          <h2 className="text-base sm:text-lg font-bold text-white">{t.title}</h2>
          <p className="text-white/80 text-xs sm:text-sm mt-1">{t.subtitle}</p>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {renderForm()}
        </div>

        {/* Footer - Fixed at bottom */}
        {selectedType && (
          <div className="p-3 sm:p-4 border-t border-gray-100 flex gap-2 sm:gap-3 flex-shrink-0 bg-white">
            <Button
              variant="outline"
              onClick={resetForm}
              className="flex-1 h-10 sm:h-11 text-sm"
            >
              {t.back}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isLoading || !isFormValid()}
              className="flex-1 h-10 sm:h-11 text-sm bg-[#5BA946] hover:bg-[#4a9339] text-white"
              data-testid="submit-section-btn"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t.add
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-scaleIn {
          animation: scaleIn 0.2s ease-out;
        }
      `}</style>
    </div>,
    document.body
  );
}
